'use client';

import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type ImgHTMLAttributes,
  type ReactNode,
} from 'react';
import { cn } from '../../utils/cn';

const ROTATE_MS = 2800;
const FADE_MS = 500;

type Layer = 'a' | 'b';

export interface RotatingPromoSlide {
  key: string;
  imgProps: ImgHTMLAttributes<HTMLImageElement>;
}

interface RotatingPromoArtProps {
  slides: RotatingPromoSlide[];
  alt: string;
  size?: number;
  shuffle?: boolean;
  fallback?: ReactNode;
  className?: string;
}

const RotatingPromoArtComponent: FC<RotatingPromoArtProps> = ({
  slides,
  alt,
  size = 104,
  shuffle = false,
  fallback,
  className,
}) => {
  const ordered = useMemo(() => {
    if (!shuffle) return slides;
    const arr = [...slides];
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }, [slides, shuffle]);

  const [slideA, setSlideA] = useState<RotatingPromoSlide | null>(null);
  const [slideB, setSlideB] = useState<RotatingPromoSlide | null>(null);
  const [current, setCurrent] = useState<Layer>('a');
  const [swapping, setSwapping] = useState(false);
  const [run, setRun] = useState(false);

  const idxRef = useRef(0);
  const currentRef = useRef<Layer>('a');
  const swappingRef = useRef(false);
  const startedRef = useRef(false);
  const imgA = useRef<HTMLImageElement>(null);
  const imgB = useRef<HTMLImageElement>(null);
  useEffect(() => { currentRef.current = current; }, [current]);
  useEffect(() => { swappingRef.current = swapping; }, [swapping]);

  useEffect(() => {
    idxRef.current = 0;
    startedRef.current = false;
    setCurrent('a');
    currentRef.current = 'a';
    setSlideA(ordered[0] ?? null);
    setSlideB(ordered[0] ?? null);
    setSwapping(false);
    setRun(false);
    if (ordered.length <= 1) return;
    const rotate = setInterval(() => {
      if (swappingRef.current) return;
      const next = (idxRef.current + 1) % ordered.length;
      idxRef.current = next;
      startedRef.current = false;
      if (currentRef.current === 'a') setSlideB(ordered[next]);
      else setSlideA(ordered[next]);
      setRun(false);
      setSwapping(true);
    }, ROTATE_MS);
    return () => clearInterval(rotate);
  }, [ordered]);

  const startAnim = useCallback(() => {
    if (!swappingRef.current || startedRef.current) return;
    startedRef.current = true;
    requestAnimationFrame(() => requestAnimationFrame(() => setRun(true)));
  }, []);

  const handleLoad = useCallback(
    (layer: Layer) => {
      const incoming: Layer = currentRef.current === 'a' ? 'b' : 'a';
      if (layer === incoming) startAnim();
    },
    [startAnim],
  );

  // WHY: у закэшированной/декодированной картинки load не срабатывает — фейд стартовать сразу при свопе
  useEffect(() => {
    if (!swapping) return;
    const incoming: Layer = currentRef.current === 'a' ? 'b' : 'a';
    const img = incoming === 'a' ? imgA.current : imgB.current;
    if (img?.complete && img.naturalWidth > 0) startAnim();
  }, [swapping, startAnim]);

  useEffect(() => {
    if (!run) return;
    const id = setTimeout(() => {
      setCurrent((c) => (c === 'a' ? 'b' : 'a'));
      setSwapping(false);
      setRun(false);
    }, FADE_MS);
    return () => clearTimeout(id);
  }, [run]);

  const layerClass = (layer: Layer) => {
    const isCurrent = current === layer;
    const base = 'absolute inset-0 m-auto transform-gpu object-contain';
    if (!swapping) return cn(base, isCurrent ? 'z-10 opacity-100' : 'z-0 opacity-0');
    if (isCurrent) {
      return cn(base, 'z-10', run ? 'transition-all duration-500 ease-out -translate-y-4 opacity-0' : 'opacity-100');
    }
    return cn(
      base,
      'z-0',
      run ? 'transition-all duration-500 ease-out translate-y-0 opacity-100' : '-translate-y-4 opacity-0',
    );
  };

  const hasArt = !!(slideA || slideB);
  const sizeStyle = { height: size, width: size };

  return (
    <span className={cn('pointer-events-none absolute', className)} style={sizeStyle}>
      {hasArt ? (
        <>
          {slideA && (
            <img
              {...slideA.imgProps}
              ref={imgA}
              alt={current === 'a' ? alt : ''}
              draggable={false}
              onLoad={() => handleLoad('a')}
              style={sizeStyle}
              className={layerClass('a')}
            />
          )}
          {slideB && (
            <img
              {...slideB.imgProps}
              ref={imgB}
              alt={current === 'b' ? alt : ''}
              draggable={false}
              onLoad={() => handleLoad('b')}
              style={sizeStyle}
              className={layerClass('b')}
            />
          )}
        </>
      ) : (
        <span className="absolute inset-0 flex items-center justify-center">{fallback}</span>
      )}
    </span>
  );
};

export const RotatingPromoArt = memo(RotatingPromoArtComponent);
